import {MapProvider} from '../../enums/map-provider.enum';
import {MapType} from '../../enums/map-type';
import {GLOBAL_REGION, MapKind, MAP_PROVIDER_DEFS} from './provider-registry';
import {HereMapsTileProvider} from './here-maps-tile-provider';
import {hereCopyright} from './here-copyright';

/** The part of the map on screen, for a credit that depends on it. */
export interface MapCreditView {
  bounds: google.maps.LatLngBoundsLiteral;
  zoom: number;
}

/**
 * One entry in the map-source picker.
 *
 * The catalog exists because this list was written out twice -- once in the
 * bottom toolbar and once in the settings sidebar -- so a provider added to
 * one silently missed the other.
 */
export interface MapSource {
  id: string;

  /** i18n key holding the display name, or the name itself for a proper noun. */
  nameKey: string;

  /** Set when the name is a proper noun with no translation. */
  literalName?: string;

  /** GLOBAL_REGION, or an ISO 3166-1 alpha-2 code. */
  region: string;

  flag: string;

  kind: MapKind;

  /** Whether picking SATELLITE actually gets aerial imagery here. */
  hasAerial: boolean;

  /**
   * The map types that draw that aerial imagery, when more do than
   * SATELLITE and HYBRID: TomTom has no terrain map and draws its photos.
   */
  aerialMapTypes?: MapType[];

  /** Credit the layer has to carry, or empty when the provider needs none. */
  attribution: string;

  /**
   * The credit for map types drawn from other data than `attribution`'s,
   * by MapType: Esri's and Mapbox's photos come from imagery companies their
   * street maps do not credit.
   */
  attributionByMapType?: {[mapType: string]: string};

  /**
   * The credit for the area and zoom on screen, for a provider whose
   * suppliers change from place to place (HERE lists them per area); null
   * until it is known, and the fixed credit above is shown meanwhile.
   */
  liveAttribution?: (mapType: string, view: MapCreditView) => string | null;
}

export interface MapSourceSection {
  region: string;
  flag: string;
  /** i18n key for the heading. */
  labelKey: string;
  sources: MapSource[];
}

/** World_Imagery's credit (ArcGIS under SATELLITE and HYBRID). */
const ESRI_IMAGERY_ATTRIBUTION =
  'Powered by Esri · Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community';

/**
 * HERE's notice is '© <this year> HERE, <its suppliers' labels>'. The
 * labels for the area on screen come from its copyright service
 * (here-copyright.ts); until they arrive, these: Maxar is the one supplier
 * of its imagery, everywhere and at every zoom.
 */
const HERE_YEAR = new Date().getFullYear();
const HERE_ATTRIBUTION = `© ${HERE_YEAR} HERE`;
const HERE_IMAGERY_ATTRIBUTION = `© ${HERE_YEAR} HERE, Maxar`;

/**
 * Worldwide providers. These reach the tile server through their own classes
 * -- keys, signatures and quadkeys -- so they are listed rather than
 * generated, unlike the national services in the registry.
 */
const GLOBAL_SOURCES: MapSource[] = [
  {
    id: MapProvider.GOOGLE_MAPS, nameKey: 'google_maps', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true, attribution: '',
  },
  // The credits below are each provider's own wording (Esri's from its
  // services' copyrightText, Mapbox's from its tilesets, TomTom's from its
  // copyright caption); their terms make showing them a condition of use.
  {
    // Terrain draws Bing's aerial layer ('a', as SATELLITE does): detection
    // refused it with "switch to satellite" over a satellite picture.
    id: MapProvider.BING_MAPS, nameKey: 'bing_maps', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true,
    attribution: '© Microsoft Corporation',
    aerialMapTypes: [MapType.SATELLITE, MapType.HYBRID, MapType.TERRAIN],
  },
  {
    id: MapProvider.MAPBOX, nameKey: 'mapbox', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true,
    attribution: '© Mapbox © OpenStreetMap',
    attributionByMapType: {
      [MapType.SATELLITE]: '© Mapbox © OpenStreetMap © Maxar',
      [MapType.HYBRID]: '© Mapbox © OpenStreetMap © Maxar',
    },
  },
  {
    // Every map type but MAP draws TomTom's own aerial imagery.
    id: MapProvider.TOMTOM, nameKey: 'tomtom', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true, attribution: '© TomTom',
    aerialMapTypes: [MapType.SATELLITE, MapType.HYBRID, MapType.TERRAIN],
  },
  // HERE only with an API key configured (hereApiKey): without one its tiles
  // cannot be fetched, and the legacy app_id/app_code API answers 410 Gone.
  ...(HereMapsTileProvider.isConfigured() ? [{
    id: MapProvider.HERE_MAPS, nameKey: '', literalName: 'HERE', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true, attribution: HERE_ATTRIBUTION,
    attributionByMapType: {
      [MapType.SATELLITE]: HERE_IMAGERY_ATTRIBUTION,
      [MapType.HYBRID]: HERE_IMAGERY_ATTRIBUTION,
    },
    liveAttribution: hereCopyright,
  }] : []),
  {
    id: MapProvider.OSM_STANDARD, nameKey: 'open_street_map', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: false,
    attribution: '© OpenStreetMap contributors',
  },
  {
    id: MapProvider.OPEN_CYCLE_MAP, nameKey: 'open_cycle_map', region: GLOBAL_REGION,
    flag: '', kind: MapKind.TOPOGRAPHIC, hasAerial: false,
    attribution: '© Thunderforest, © OpenStreetMap contributors',
  },
  // Esri's two street maps: its source lists, with the OpenStreetMap credit
  // (ODbL) moved up front. Last in the list, it was past the point where the
  // one-line credit is cut short (the whole of it shows on a click).
  {
    // World_Topo_Map, and World_Imagery under SATELLITE and HYBRID.
    id: MapProvider.ARC_GIS, nameKey: 'arcgis', region: GLOBAL_REGION,
    flag: '', kind: MapKind.STREET, hasAerial: true,
    attribution: 'Powered by Esri · © OpenStreetMap contributors · Sources: Esri, HERE, Garmin, Intermap, '
      + 'increment P Corp., GEBCO, USGS, FAO, NPS, NRCAN, GeoBase, IGN, Kadaster NL, Ordnance Survey, '
      + 'Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community',
    attributionByMapType: {
      [MapType.SATELLITE]: ESRI_IMAGERY_ATTRIBUTION,
      [MapType.HYBRID]: ESRI_IMAGERY_ATTRIBUTION,
    },
  },
  {
    id: MapProvider.ARCGIS_WORLD_STREET_MAP, nameKey: 'world_street_map',
    region: GLOBAL_REGION, flag: '', kind: MapKind.STREET, hasAerial: false,
    attribution: 'Powered by Esri · © OpenStreetMap contributors · Sources: Esri, HERE, Garmin, USGS, Intermap, '
      + 'INCREMENT P, NRCan, Esri Japan, METI, Esri China (Hong Kong), Esri Korea, Esri (Thailand), NGCC, '
      + 'and the GIS User Community',
  },
  // Not Yandex: its tiles use the ellipsoidal Mercator grid (EPSG:3395) and
  // drew about 20 km off on this Web Mercator map, and its terms keep its
  // tiles to its own API.
];

/**
 * Providers that only cover one country. USGS sits here rather than under
 * Global because it has no tiles outside the United States -- it was in the
 * worldwide list, which is why picking it anywhere else went blank.
 */
const NATIONAL_SOURCES: MapSource[] = [
  {
    id: MapProvider.USGS, nameKey: 'usgs', region: 'US', flag: '\u{1F1FA}\u{1F1F8}',
    kind: MapKind.TOPOGRAPHIC, hasAerial: true,
    attribution: '© U.S. Geological Survey',
  },
  ...MAP_PROVIDER_DEFS.map(def => ({
    id: def.id as string,
    nameKey: '',
    literalName: def.name,
    region: def.region,
    flag: def.flag,
    kind: def.kind,
    hasAerial: def.hasAerial,
    attribution: def.attribution,
  })),
];

export const ALL_MAP_SOURCES: MapSource[] = [...GLOBAL_SOURCES, ...NATIONAL_SOURCES];

export const MAP_SOURCE_BY_ID = new Map<string, MapSource>(
  ALL_MAP_SOURCES.map(source => [source.id, source]));

/**
 * The credit to show for `source` drawn as `mapType` -- for the part of the
 * map in `view`, when given and the source credits by area.
 */
export function mapSourceAttribution(source: MapSource | undefined, mapType: string | undefined,
                                     view?: MapCreditView): string {
  if (!source) {
    return '';
  }
  const live = view && mapType && source.liveAttribution ? source.liveAttribution(mapType, view) : null;
  if (live) {
    return live;
  }
  const byType = source.attributionByMapType;
  return (byType && mapType && byType[mapType]) || source.attribution;
}

/** i18n key for a region heading. */
export function regionLabelKey(region: string): string {
  return region === GLOBAL_REGION ? 'global' : 'country_' + region.toLowerCase();
}

/**
 * The picker's sections: worldwide first, then each country by its own
 * translated name, so a Spanish user reads Alemania before Espana rather than
 * following English ordering.
 */
export function buildMapSourceSections(
  translate: (key: string) => string,
  filter = ''): MapSourceSection[] {

  const needle = filter.trim().toLowerCase();
  const matches = (source: MapSource) => {
    if (!needle) {
      return true;
    }
    const name = (source.literalName || translate(source.nameKey) || '').toLowerCase();
    const region = translate(regionLabelKey(source.region)).toLowerCase();
    return name.includes(needle) || region.includes(needle);
  };

  const byRegion = new Map<string, MapSource[]>();
  ALL_MAP_SOURCES.filter(matches).forEach(source => {
    const bucket = byRegion.get(source.region);
    if (bucket) {
      bucket.push(source);
    } else {
      byRegion.set(source.region, [source]);
    }
  });

  const sections: MapSourceSection[] = [];
  const global = byRegion.get(GLOBAL_REGION);
  if (global) {
    sections.push({
      region: GLOBAL_REGION, flag: '',
      labelKey: regionLabelKey(GLOBAL_REGION), sources: global,
    });
    byRegion.delete(GLOBAL_REGION);
  }

  Array.from(byRegion.entries())
    .map(([region, sources]) => ({
      region, sources, flag: sources[0].flag, labelKey: regionLabelKey(region),
    }))
    .sort((a, b) => translate(a.labelKey).localeCompare(translate(b.labelKey)))
    .forEach(section => sections.push(section));

  return sections;
}
